import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import './CategoryMaster.css';

const STATUS_OPTIONS = [
    { label: 'Active',   value: 'Active'   },
    { label: 'Inactive', value: 'Inactive' }
];

const ModuleGroupDialog = ({ visible, groupData, onHide, onSave }) => {
    const [form, setForm]     = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!visible) return;
        setErrors({});
        if (groupData) {
            setForm({
                module_group_id:   groupData.module_group_id,
                module_group_name: groupData.module_group_name || '',
                sort_id:           groupData.sort_id != null ? Number(groupData.sort_id) : null,
                icon:              groupData.icon || '',
                status:            groupData.status || 'Active'
            });
        } else {
            setForm({ module_group_name: '', sort_id: null, icon: '', status: 'Active' });
        }
    }, [visible, groupData]);

    const update = (field, val) => {
        setForm((prev) => ({ ...prev, [field]: val }));
        if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    };

    const validate = () => {
        const errs = {};
        if (!form.module_group_name?.trim()) errs.module_group_name = 'Module Group Name is required';
        if (form.sort_id == null || isNaN(form.sort_id)) errs.sort_id = 'Sort ID is required';
        if (!form.icon?.trim()) errs.icon = 'Icon is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        onSave({
            module_group_id:   form.module_group_id || null,
            module_group_name: form.module_group_name.trim(),
            sort_id:           form.sort_id,
            icon:              form.icon.trim(),
            status:            form.status,
            updated_by:        localStorage.getItem('userid') || ''
        });
    };

    const isEdit = !!groupData?.module_group_id;

    const dialogHeader = (
        <div className="sub-dialog-header">
            <span className="sub-dialog-header-icon">
                <i className="pi pi-th-large" />
            </span>
            <div>
                <div className="sub-dialog-header-text">
                    {isEdit ? 'Edit Module Group' : 'Add Module Group'}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                    {isEdit ? 'Update the module group details below' : 'Enter new module group details below'}
                </div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="sub-dialog-cancel" onClick={onHide} />
            <Button label={isEdit ? 'Update' : 'Save'} icon="pi pi-check" className="sub-dialog-save" onClick={handleSave} />
        </div>
    );

    return (
        <Dialog
            header={dialogHeader}
            visible={visible}
            style={{ width: '480px' }}
            modal
            position="center"
            footer={dialogFooter}
            onHide={onHide}
            draggable={false}
            resizable={false}
            className="sub-master-dialog p-fluid"
        >
            <div className="sub-form-grid">

                {/* Module Group Name — full width */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-tag sub-field-label-icon" />
                        Module Group Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputText
                        value={form.module_group_name || ''}
                        onChange={(e) => update('module_group_name', e.target.value)}
                        placeholder="e.g. Sales"
                        className={`w-full${errors.module_group_name ? ' p-invalid' : ''}`}
                    />
                    {errors.module_group_name && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.module_group_name}
                        </span>
                    )}
                </div>

                {/* Sort ID */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-sort-numeric-up sub-field-label-icon" />
                        Sort ID <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputNumber
                        value={form.sort_id}
                        onValueChange={(e) => update('sort_id', e.value)}
                        min={0}
                        useGrouping={false}
                        className={`w-full${errors.sort_id ? ' p-invalid' : ''}`}
                        inputStyle={{ width: '100%' }}
                    />
                    {errors.sort_id && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.sort_id}
                        </span>
                    )}
                </div>

                {/* Icon */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-star sub-field-label-icon" />
                        Icon <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputText
                        value={form.icon || ''}
                        onChange={(e) => update('icon', e.target.value)}
                        placeholder="e.g. pi pi-home"
                        className={`w-full${errors.icon ? ' p-invalid' : ''}`}
                    />
                    {errors.icon && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.icon}
                        </span>
                    )}
                </div>

                {/* Status — full width */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-circle sub-field-label-icon" />
                        Status
                    </label>
                    <Dropdown
                        value={form.status || 'Active'}
                        options={STATUS_OPTIONS}
                        onChange={(e) => update('status', e.value)}
                        className="w-full"
                    />
                </div>

            </div>
        </Dialog>
    );
};

export default ModuleGroupDialog;
